/**
 * AI Cart Recommendations — Emma AI
 * Custom element that fetches personalised product recommendations
 * from Emma API and renders them in the cart drawer.
 *
 * Features:
 * - MutationObserver on cart drawer visibility
 * - Session context (intent signals, UTM, traffic source)
 * - DOM-based rendering (no innerHTML for XSS safety)
 * - sessionStorage caching (5-min TTL)
 * - GA4 + Klaviyo event tracking
 * - A/B variant support
 * - Graceful error handling (hides section on failure)
 */
(function () {
  'use strict';

  var CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  var API_TIMEOUT = 3000; // 3 seconds
  var CACHE_KEY_PREFIX = 'emma_cart_recs_';

  // ---- Helpers ----

  /** Create a DOM element with attributes and optional text */
  function el(tag, attrs, text) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var key in attrs) {
        if (key === 'className') node.className = attrs[key];
        else if (key === 'style' && typeof attrs[key] === 'object') {
          for (var s in attrs[key]) node.style[s] = attrs[key][s];
        } else node.setAttribute(key, attrs[key]);
      }
    }
    if (text != null) node.textContent = String(text);
    return node;
  }

  /** Format price in pennies to money string using Shopify money format */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '\u00a3{{amount}}';

    function addCommas(n) {
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = addCommas((cents / 100).toFixed(2));
        break;
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100));
        break;
      case 'amount_with_comma_separator':
        value = (cents / 100).toFixed(2).replace('.', ',');
        break;
      default:
        value = addCommas((cents / 100).toFixed(2));
    }

    return formatString.replace(placeholderRegex, value);
  }

  /** Generate a simple hash of sorted product IDs for cache key */
  function hashIds(ids) {
    var sorted = ids.slice().sort();
    var str = sorted.join(',');
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(36);
  }

  /** Get cached recommendations from sessionStorage */
  function getCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (Date.now() - data.ts > CACHE_TTL) {
        sessionStorage.removeItem(key);
        return null;
      }
      return data.products;
    } catch (e) {
      return null;
    }
  }

  /** Store recommendations in sessionStorage */
  function setCache(key, products) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), products: products }));
    } catch (e) { /* quota exceeded -- ignore */ }
  }

  /** Fire GA4 event via dataLayer */
  function fireGA4(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    } catch (e) { /* silent */ }
  }

  /** Fire Klaviyo event */
  function fireKlaviyo(eventName, data) {
    try {
      var lq = window._learnq || [];
      lq.push(['track', eventName, data]);
    } catch (e) { /* silent */ }
  }

  /** Fire impression/click tracking to Emma API */
  function trackToEmma(apiEndpoint, eventType, data) {
    try {
      var ctx = window.__emmaContext || {};
      var payload = {
        sessionId: ctx.session_id || 'unknown',
        event: eventType,
        data: data
      };
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          apiEndpoint + '/api/track/event',
          new Blob([JSON.stringify(payload)], { type: 'application/json' })
        );
      } else {
        fetch(apiEndpoint + '/api/track/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true
        }).catch(function () {});
      }
    } catch (e) { /* silent */ }
  }

  // ---- Custom Element ----

  if (typeof customElements === 'undefined') return;

  var AiCartRecommendations = /** @class */ (function () {
    function AiCartRecommendations() {
      var self = (typeof Reflect !== 'undefined' && Reflect.construct)
        ? Reflect.construct(HTMLElement, [], AiCartRecommendations)
        : HTMLElement.call(this) || this;
      self._loaded = false;
      self._currentCartHash = null;
      self._observer = null;
      return self;
    }

    // Inherit from HTMLElement
    AiCartRecommendations.prototype = Object.create(HTMLElement.prototype);
    AiCartRecommendations.prototype.constructor = AiCartRecommendations;

    AiCartRecommendations.prototype.connectedCallback = function () {
      var self = this;
      this._apiEndpoint = this.getAttribute('data-api-endpoint') || 'https://emma-api.skintique.co.uk';
      this._moneyFormat = (this.getAttribute('data-money-format') || '\u00a3{{amount}}').replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      this._productsContainer = this.querySelector('.ai-cart-recs__products');

      // Watch for cart drawer opening via aria-hidden attribute
      var drawer = document.getElementById('cart-drawer');
      if (drawer) {
        this._observer = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            if (mutations[i].attributeName === 'aria-hidden') {
              var isVisible = drawer.getAttribute('aria-hidden') === 'false';
              if (isVisible) {
                self._onDrawerOpen();
              }
            }
          }
        });
        this._observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
      }

      // Also listen for cart update events (items added/removed)
      document.addEventListener('dispatch:cart-drawer:refresh', function () {
        self._currentCartHash = null; // invalidate cache
      });
    };

    AiCartRecommendations.prototype.disconnectedCallback = function () {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    };

    AiCartRecommendations.prototype._onDrawerOpen = function () {
      var self = this;

      // Fetch current cart
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart.items || cart.items.length === 0) {
            self.style.display = 'none';
            return;
          }

          // Extract product IDs (deduplicated)
          var productIds = [];
          var seen = {};
          for (var i = 0; i < cart.items.length; i++) {
            var pid = String(cart.items[i].product_id);
            if (!seen[pid]) {
              seen[pid] = true;
              productIds.push(pid);
            }
          }

          // Check if cart changed since last load
          var cartHash = hashIds(productIds);
          if (cartHash === self._currentCartHash && self._loaded) {
            self.style.display = '';
            return; // same cart, already loaded
          }

          // Check sessionStorage cache
          var cacheKey = CACHE_KEY_PREFIX + cartHash;
          var cached = getCache(cacheKey);
          if (cached && cached.length > 0) {
            self._currentCartHash = cartHash;
            self._renderProducts(cached);
            return;
          }

          // Show skeleton, fetch from API
          self.style.display = '';
          self.removeAttribute('data-loaded');

          self._fetchRecommendations(productIds, cart.total_price, cart.item_count)
            .then(function (result) {
              if (!result || !result.products || result.products.length === 0) {
                self.style.display = 'none';
                return;
              }

              // A/B variant: control hides the section
              if (result.abVariant === 'control') {
                self.style.display = 'none';
                return;
              }

              self._currentCartHash = cartHash;
              setCache(cacheKey, result.products);
              self._renderProducts(result.products);
            })
            .catch(function () {
              // API error -- hide gracefully
              self.style.display = 'none';
            });
        })
        .catch(function () {
          self.style.display = 'none';
        });
    };

    AiCartRecommendations.prototype._fetchRecommendations = function (productIds, cartTotal, itemCount) {
      var self = this;
      var ctx = window.__emmaContext || {};
      var customer = window.__skintique_customer || {};

      // Gather session context from emma-tracker if available
      var sessionSummary = {};
      if (window.emmaTracker && typeof window.emmaTracker.getSessionSummary === 'function') {
        sessionSummary = window.emmaTracker.getSessionSummary();
      }

      var body = {
        productIds: productIds,
        sessionId: ctx.session_id || sessionSummary.sessionId || 'unknown',
        cartValue: (cartTotal || 0) / 100,
        cartItemCount: itemCount || 0,
        limit: 4
      };

      // Optional fields
      if (customer.customerId) body.customerId = String(customer.customerId);
      if (sessionSummary.trafficSource) body.trafficSource = sessionSummary.trafficSource;
      if (sessionSummary.utmParams) body.utmParams = sessionSummary.utmParams;
      if (sessionSummary.duration) body.sessionDuration = sessionSummary.duration;

      // Fetch with timeout
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timeoutId = setTimeout(function () {
        if (controller) controller.abort();
      }, API_TIMEOUT);

      var fetchOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      };
      if (controller) fetchOptions.signal = controller.signal;

      return fetch(self._apiEndpoint + '/api/cart-recommendations', fetchOptions)
        .then(function (r) {
          clearTimeout(timeoutId);
          if (!r.ok) throw new Error('API error ' + r.status);
          return r.json();
        })
        .catch(function (err) {
          clearTimeout(timeoutId);
          throw err;
        });
    };

    AiCartRecommendations.prototype._renderProducts = function (products) {
      var self = this;
      var container = this._productsContainer;
      if (!container) return;

      // Clear previous
      while (container.firstChild) container.removeChild(container.firstChild);

      var ga4Items = [];

      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        var card = self._buildCard(p, i);
        container.appendChild(card);

        ga4Items.push({
          item_id: p.shopifyId || p.handle,
          item_name: p.title,
          item_brand: p.vendor,
          price: parseFloat(p.price) || 0,
          index: i,
          item_list_name: 'emma_cart_recommendations'
        });
      }

      // Mark as loaded (hides skeleton, shows products)
      this.setAttribute('data-loaded', '');
      this._loaded = true;
      this.style.display = '';

      // Fire GA4 view_item_list
      fireGA4('view_item_list', {
        item_list_name: 'emma_cart_recommendations',
        items: ga4Items
      });

      // Fire Klaviyo event
      fireKlaviyo('Viewed Recommendation', {
        source: 'cart_drawer',
        products: products.map(function (p) {
          return { title: p.title, handle: p.handle, price: p.price };
        })
      });

      // Fire impression tracking to Emma
      trackToEmma(this._apiEndpoint, 'rec_impression', {
        source: 'cart_drawer',
        productIds: products.map(function (p) { return p.shopifyId; })
      });
    };

    AiCartRecommendations.prototype._buildCard = function (product, index) {
      var self = this;
      var card = el('div', { className: 'ai-cart-rec-card', role: 'listitem' });

      // Image
      var mediaWrap = el('div', { className: 'ai-cart-rec-card__media' });
      var imgUrl = product.imageUrl || '';
      if (imgUrl) {
        // Use Shopify CDN resizing for performance
        var sizedUrl = imgUrl.replace(/\.([^.]+)$/, '_180x.$1');
        var img = el('img', {
          src: sizedUrl,
          alt: product.title || '',
          loading: 'lazy',
          width: '72',
          height: '72'
        });
        mediaWrap.appendChild(img);
      }
      card.appendChild(mediaWrap);

      // Info
      var info = el('div', { className: 'ai-cart-rec-card__info' });

      // Vendor
      if (product.vendor) {
        info.appendChild(el('div', { className: 'ai-cart-rec-card__vendor' }, product.vendor));
      }

      // Title with link
      var titleWrap = el('div', { className: 'ai-cart-rec-card__title' });
      var titleLink = el('a', {
        href: '/products/' + (product.handle || ''),
        className: 'ai-cart-rec-card__title-link'
      }, product.title || '');

      // Track click
      titleLink.addEventListener('click', function () {
        fireGA4('select_item', {
          item_list_name: 'emma_cart_recommendations',
          items: [{
            item_id: product.shopifyId || product.handle,
            item_name: product.title,
            item_brand: product.vendor,
            price: parseFloat(product.price) || 0,
            index: index
          }]
        });
        fireKlaviyo('Clicked Recommendation', {
          source: 'cart_drawer',
          title: product.title,
          handle: product.handle,
          price: product.price
        });
        trackToEmma(self._apiEndpoint, 'rec_click', {
          productId: product.shopifyId,
          handle: product.handle,
          position: index,
          source: 'cart_drawer'
        });
      });
      titleWrap.appendChild(titleLink);
      info.appendChild(titleWrap);

      // Price row
      var priceRow = el('div', { className: 'ai-cart-rec-card__price-row' });
      var priceVal = parseFloat(product.price);
      if (!isNaN(priceVal)) {
        var priceInCents = Math.round(priceVal * 100);
        priceRow.appendChild(el('span', { className: 'ai-cart-rec-card__price' },
          formatMoney(priceInCents, self._moneyFormat)));

        // Compare-at price
        if (product.compareAtPrice) {
          var compareVal = parseFloat(product.compareAtPrice);
          if (!isNaN(compareVal) && compareVal > priceVal) {
            priceRow.appendChild(el('span', { className: 'ai-cart-rec-card__compare-price' },
              formatMoney(Math.round(compareVal * 100), self._moneyFormat)));
          }
        }
      }
      info.appendChild(priceRow);

      // Add to Basket button
      var addBtn = el('button', {
        className: 'ai-cart-rec-card__add-btn',
        type: 'button'
      }, 'Add to Basket');

      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (addBtn.disabled) return;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding...';

        // Fetch product JSON to get first available variant ID
        fetch('/products/' + product.handle + '.js')
          .then(function (r) { return r.json(); })
          .then(function (productData) {
            // Find first available variant
            var variantId = null;
            if (productData.variants) {
              for (var v = 0; v < productData.variants.length; v++) {
                if (productData.variants[v].available) {
                  variantId = productData.variants[v].id;
                  break;
                }
              }
              if (!variantId && productData.variants.length > 0) {
                variantId = productData.variants[0].id;
              }
            }
            if (!variantId) throw new Error('No variant');

            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
          })
          .then(function (r) {
            if (!r.ok) throw new Error('Add failed');
            return r.json();
          })
          .then(function () {
            addBtn.textContent = 'Added!';
            setTimeout(function () {
              addBtn.textContent = 'Add to Basket';
              addBtn.disabled = false;
            }, 1500);

            // Refresh cart drawer
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

            // Invalidate cache
            self._currentCartHash = null;

            // Track add-to-cart
            fireGA4('add_to_cart', {
              currency: self.getAttribute('data-currency') || 'GBP',
              value: parseFloat(product.price) || 0,
              items: [{
                item_id: product.shopifyId || product.handle,
                item_name: product.title,
                item_brand: product.vendor,
                price: parseFloat(product.price) || 0,
                quantity: 1,
                item_list_name: 'emma_cart_recommendations'
              }]
            });
            fireKlaviyo('Added from Recommendation', {
              source: 'cart_drawer',
              title: product.title,
              handle: product.handle,
              price: product.price
            });
            trackToEmma(self._apiEndpoint, 'rec_add_to_cart', {
              productId: product.shopifyId,
              handle: product.handle,
              position: index,
              source: 'cart_drawer'
            });
          })
          .catch(function () {
            addBtn.textContent = 'Error';
            setTimeout(function () {
              addBtn.textContent = 'Add to Basket';
              addBtn.disabled = false;
            }, 2000);
          });
      });

      info.appendChild(addBtn);
      card.appendChild(info);

      return card;
    };

    return AiCartRecommendations;
  })();

  // Register the custom element
  try {
    customElements.define('ai-cart-recommendations', AiCartRecommendations);
  } catch (e) {
    // Already defined or unsupported
    console.warn('[Emma] Could not register ai-cart-recommendations:', e.message);
  }
})();
